import { useDispatch } from 'react-redux';
import {
    Col,
    Alert,
    Spinner,
    CardBody,
    Card,
    CardHeader,
    Row,
} from 'reactstrap';
import { useNavigate, Link } from 'react-router-dom';
import { createSelector } from 'reselect';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { ODataGrid } from '../../Components/OElements/ODataGrid.js';
import { getPersonAssignedItemsSummary, getPersonAssignedItemsSummaryAll, getPersonAssignedItemsSummaryQuery, getPersonAssignmentItemsQuery, getPersonAssignmentsQuery } from '../../slices/thunks.js';
import { useUser } from '../../Components/Hooks/useUserHooks.js';
import { Badge, Box } from '@mantine/core';
import { toSmartTitleCase } from '../../utils/toSmartTitleCase.js';
import { getPriceBadgeColor, getTotalCostBadgeColor } from './constants.js';

const AssignmentsList = ({personId}) => {
    const dispatch = useDispatch();
    const history = useNavigate();
    const { user } = useUser();

    const columns = useMemo(
        () => [
            {
                header: 'Código',
                accessorKey: 'warehouse_item.code_sap',
                size: 'auto',
            },
            {
                header: 'Nombre',
                accessorKey: 'name',
                size: 'auto',
            },
            {
                header: 'Talla',
                accessorKey: 'size',
                size: 'auto',
            },
            {
                header: 'Color',
                accessorKey: 'color',
                size: 'auto',
            },
            {
                header: 'Unidad',
                accessorKey: 'unit_measurement_name',
                size: 'auto',
            },
            {
                header: 'Cantidad',
                accessorKey: 'quantity',
                size: 'auto',
            },
            {
                header: 'Devueltos',
                accessorKey: 'quantity_returned',
                size: 'auto',
            },
            {
                header: 'Disponibles',
                accessorKey: 'quantity_available',
                size: 'auto',
            },
            
            {
                header: 'Precio Unit.',
                accessorKey: 'price',
                size: 'auto',
                align: 'right',
                Cell: cellProps => {
                    const cellValue = cellProps.row.original.price;

                    return (
                        <Badge
                            color={getPriceBadgeColor(cellValue)}
                            variant='light'
                            tt='capitalize'
                        >
                            $ {cellValue}
                        </Badge>
                    );
                },
            },
            {
                header: 'Costo Total',
                accessorKey: 'item_total_cost',
                size: 'auto',
                align: 'right',
                Cell: cellProps => {
                    const cellValue = cellProps.row.original.item_total_cost;

                    return (
                        <Badge
                            color={getTotalCostBadgeColor(cellValue)}
                            variant='light'
                            tt='capitalize'
                        >
                            $ {cellValue}
                        </Badge>
                    );
                },
            },
            {
                header: 'Código de Asignación',
                accessorKey: 'code',
                size: 'auto',
            },
            {
				header: 'Estado',
				accessorKey: 'personal_protective_equipment_status.name',
				size: 'auto',
				dataType: 'multi-select',
				accessorFn: row => {
					return row.personal_protective_equipment_status.name;
				},
				Cell: ({ row }) => {
					let status = row.original.personal_protective_equipment_status;
					let bgColor = status?.bgcolor;
					let txColor = status?.txcolor;
					return (
						<div>
							<span
								className={`badge`}
								style={{ backgroundColor: bgColor, color: txColor }}
							>
								{status ? status.name : ''}
							</span>
						</div>
					);
				},
			},
			{
				header: 'Fecha',
				accessorKey: 'assignment_date',
				dataType: 'date',
				size: 'auto',
				Cell: ({ row }) => {
					let status =
						row.original.personal_protective_equipment_status.code;
					let date = row.original.assignment_date;
					if (status == 'DVT') {
						date = row.original.return_date;
					}

					return new Date(date).toLocaleDateString('es-CL', {
						year: 'numeric',
						month: '2-digit',
						day: '2-digit',
					});
				},
			},
			{
				header: 'Entrega',
				accessorKey: 'delivery_date',
				dataType: 'date',
				size: 'auto',
			},
            { header: 'Motivo', accessorKey: 'personal_protective_equipment_reason.name', size: 'auto', dataType: 'multi-select', },
			{ header: 'Doc Num', accessorKey: 'doc_num', size: 'auto', },
			{ header: 'Doc Entry', accessorKey: 'doc_entry', size: 'auto', },
            { header: 'Establecimiento', accessorKey: 'establishment.name', size: 'auto', dataType: 'multi-select', },
            {
                header: 'Origen',
                accessorKey: 'warehouse_name',
                size: 'auto',
                size: 'auto',
                dataType: 'multi-select',
                accessorFn: row => {
                    let status = row.personal_protective_equipment_status.code;
                    let warehouse_name = row.warehouse?.name;
                    if (status == 'DVT') {
                        warehouse_name = row.establishment?.name;
                    }
                    return warehouse_name;
                },
                Cell: ({ row }) => {
                    let status =
                        row.original.personal_protective_equipment_status.code;
                    let warehouse_name = toSmartTitleCase(
                        row.original.warehouse?.name
                    );
                    if (status == 'DVT') {
                        warehouse_name = toSmartTitleCase(
                            row.original.establishment?.name
                        );
                    }
                    return warehouse_name;
                },
            },
            {
                header: 'Destino',
                accessorKey: 'establishment_name',
                size: 'auto',
                size: 'auto',
                dataType: 'multi-select',
                accessorFn: row => {
                    let status = row.personal_protective_equipment_status.code;
                    let establishment_name = row.establishment?.name;
                    if (status == 'DVT') {
                        establishment_name = row.warehouse?.name;
                    }
                    return establishment_name;
                },
                Cell: ({ row }) => {
                    let status =
                        row.original.personal_protective_equipment_status.code;
                    let establishment_name = row.original.establishment?.name;
                    if (status == 'DVT') {
                        establishment_name = row.original.warehouse?.name;
                    }
                    return establishment_name;
                },
            },
        ],
    );

    return (
        <React.Fragment>
            <Box
                className='p-2'
                sx={{
                    display: 'Grid',
                    margin: 'auto',
                    width: '100%',
                }}
            >
                <ODataGrid
                    columns={columns}
                    getDataFunc={getPersonAssignmentItemsQuery}
                    nameFile={`items_asignados_persona_${personId}`}
                    queryPopulate={[
                        'establishment',
                        'person',
                        'warehouse_item',
                        'warehouse',
                        'personal_protective_equipment_status'
                    ]}
                    baseFilters={{
						person_id: { $eq: personId },
					}}
                    remoteOperations={{
                        pagination: true,
                        sorting: true,
                        filtering: true,
                    }}
                    getRowIdTable={row => row._id}
                />
            </Box>
        </React.Fragment>
    );
}

export default AssignmentsList;